import { ControlState, StateSystem } from "../../../shared/systems/stateSystem";
import { Uninitialized, getSystem } from "../../../shared/utils";
import { SyncMessageType } from "../../../shared/messageTypes";
import { GameClient } from "../gameClient";
import { Player } from "../../../shared/player";
import { LifeAndDeathSystem } from "../../../shared/systems/lifeAndDeathSystem";
import { CameraZoomLevel, PlayerConstants, Speeds } from "../../../shared/constants";
import { ClientSyncSystem } from "../systems/clientSyncSystem";
import { ClientRecapState } from "./clientRecapState";
import { GameState } from "../../../shared/game";
import { ClientEndGameState } from "./clientEndGameState";
import { commonEndSimulation, commonStartSimulation } from "../../../shared/common";
import { SoundSystem } from "../systems/soundSystem";
import { CameraSystem } from "../systems/cameraSystem";
import { vec2 } from "@tlaukkan/tsm";
import * as AchievementTracker from "../AchievementTracker";
import { deserializeTiebreaker } from "../../../shared/synchronization";

export class ClientSimulationState extends ControlState {
	isComplete = false;

	private controller: StateSystem = Uninitialized;
	private clientSyncSystem!: ClientSyncSystem;
	private player: Player = Uninitialized;
	private playersAlive: Array<number> = Uninitialized;

	private deathSystem: LifeAndDeathSystem = Uninitialized;
	private lastFrameNumberOfBloodCells = 0;
	private endStepNumber = -1;

	private cameraSystem: CameraSystem = Uninitialized;

	getName(): GameState {
		return "simulation"
	}

	constructor(private gameClient: GameClient) {
		super();
	}

	onEnter(controller: StateSystem): void {
		this.controller = controller;
		this.clientSyncSystem = getSystem(controller.world, ClientSyncSystem);
		this.cameraSystem = getSystem(controller.world, CameraSystem);

		//Disable the camera for one frame so initial movement starts AFTER 
		//the very slow first frame of simulation when everything is intialized
		this.cameraSystem.stop();
		setTimeout(() => { this.cameraSystem.play() }, 1);

		this.player = this.gameClient.getPlayer()!;

		this.clientSyncSystem.maximumStep = -1;
		this.deathSystem = getSystem(this.gameClient.world, LifeAndDeathSystem);
		const startingHp = PlayerConstants.StartingHpByTrainingLevel[this.gameClient.trainingLevel];
		this.lastFrameNumberOfBloodCells = startingHp * this.gameClient.requiredPlayers;
		this.gameClient.numberOfBloodCells = this.lastFrameNumberOfBloodCells;

		this.deathSystem.queueResetWorldToComposition();

		commonStartSimulation(this.gameClient);

		const soundSystem = getSystem(controller.world, SoundSystem);
		//soundSystem.playM('simulation');
		soundSystem.switchBackgroundMusic('simulation');

		// Clear any inspection stuff at the start of the simulation
		this.gameClient.setInspection(null);

		//Speed up trails degredation
		this.gameClient.renderEngine.background.accelerateTrails(1.0);
		this.gameClient.toggleAutoCamera(true);
	}


	onExit(controller: StateSystem): void {
		commonEndSimulation(this.gameClient);

		if (!this.playersAlive) {
			this.controller.pushState(new ClientRecapState(this.gameClient));
		} else {
			this.controller.pushState(new ClientEndGameState(this.gameClient));
		}
		this.gameClient.tiebreakerRound = 0;
		this.clientSyncSystem.maximumStep = this.endStepNumber;
	}

	update(delta: number): void {
		if (this.gameClient.numberOfBloodCells !== this.lastFrameNumberOfBloodCells) {
			this.gameClient.updateUI();
			this.lastFrameNumberOfBloodCells = this.gameClient.numberOfBloodCells;
		}
		const end = this.gameClient.messageBuffer?.dequeueMessage(SyncMessageType.GotoShop);
		if (end) {
			//console.log("Go to shop");
			this.endStepNumber = end.stepNumber;
			this.gameClient.lastRoundSurvivors = end.survivors;
			this.gameClient.latestVirusGrowth = end.virusGrowth || null;
			this.isComplete = true;
			this.gameClient.round = end.newRoundNumber;
			return;
		}

		const roundStats = this.gameClient.messageBuffer?.dequeueMessage(SyncMessageType.RoundStats);
		if (roundStats) {
			this.gameClient.roundStats = roundStats.stats;
			AchievementTracker.onEndCombat(this.gameClient);
		}

		const gameEnd = this.gameClient.messageBuffer?.dequeueMessage(SyncMessageType.GotoLobby);
		if (gameEnd) {
			//This game is over, so its autosave shouldn't be offered as a Continue. Only the slot
			//for the kind of game that just ended: a finished solo game mustn't discard a
			//multiplayer game waiting to be resumed.
			localStorage.removeItem(this.gameClient.autosaveName());
			this.playersAlive = gameEnd.playersAlive;
			this.gameClient.setWinners(this.playersAlive);
			this.isComplete = true;
			return;
		}

		const nextWave = this.gameClient.messageBuffer?.getUnorderedMessage(SyncMessageType.NextLane, getStepNumber());
		if (nextWave) {
			this.gameClient.nextVirusLane(nextWave.playerId, nextWave.waveNumbers);
		}

		const tiebreaker = this.gameClient.messageBuffer?.getUnorderedMessage(SyncMessageType.Tiebreaker, getStepNumber());
		if (tiebreaker) {
			this.gameClient.toggleAutoCamera(false);
			this.cameraSystem.setZoomLevel(CameraZoomLevel.Medium);
			this.announceTiebreakerRound(tiebreaker.tiebreakerRound);

			const { cellsIncreased, cellsAdded, owningPlayerId } = deserializeTiebreaker(tiebreaker, this.gameClient);

			//Apply the growth to existing entites
			cellsIncreased.forEach(cell => cell && this.gameClient.applyExistingEntityTiebreakerGrowth(cell));

			//Create new cells (unsynced, client will repeat this on getting tiebreaker message)
			for (const cell of cellsAdded) {
				const player = this.gameClient.players.find(p => p.id === owningPlayerId);
				if (!player) throw new Error("Tiebreaker infected cells added to non-existant player id");
				this.gameClient.addCompositionElement(player.enemyCells, cell);
			}
			//The added cells will be instatiated by the sync system automatically
		}

		//Is battle round
		if (this.gameClient.roundRewardDisplay) {
			//Assumes two players here
			const self = this.gameClient.getPlayer()!;
			const opponent = this.gameClient.players.find(p => p.id !== self.id)!;
			const playerScore = this.gameClient.lostCellsPerPlayerThisRound.get(self.id) || 0;
			const opponentScore = this.gameClient.lostCellsPerPlayerThisRound.get(opponent.id) || 0;
			if (playerScore !== this.gameClient.roundRewardDisplay.leftScore || opponentScore !== this.gameClient.roundRewardDisplay.rightScore) {
				this.gameClient.roundRewardDisplay.leftScore = playerScore;
				this.gameClient.roundRewardDisplay.rightScore = opponentScore;
				this.gameClient.updateUI();
			}
		}
	}

	async announceTiebreakerRound(tiebreakerRound: number) {
		this.gameClient.pause();

		//Focus camera on the midpoint of the two players
		this.cameraSystem.controlsLocked = true;
		this.cameraSystem.setZoomLevel(CameraZoomLevel.Medium);
		const player1 = this.gameClient.players[0];
		const player2 = this.gameClient.players[1];
		const player1Pos = player1.spawnPoint
		const player2Pos = player2.spawnPoint;
		const mid = [(player1Pos[0] + player2Pos[0]) / 2, (player1Pos[1] + player2Pos[1]) / 2] as [number, number];
		this.cameraSystem.focusOnPosition(new vec2(mid));

		//This would be cool, escalate the music?
		//this.soundSystem.switchBackgroundMusic();

		//TODO: Show the infected modal here
		await this.showTiebreakerAnnouncement(tiebreakerRound);

		this.cameraSystem.controlsLocked = false;
		this.gameClient.unpause();
	}

	async showTiebreakerAnnouncement(tiebreakerRound: number) {
		return new Promise<void>((resolve, reject) => {
			this.gameClient.showTiebreakerAnnouncement = true;
			this.gameClient.tiebreakerRound = tiebreakerRound;
			this.gameClient.onConfirmVirusGrowth = () => {
				if (this.gameClient.tutorialStateMachine.tutorialWantsClicks()) return;
				this.gameClient.showTiebreakerAnnouncement = false;
				this.gameClient.updateUI();
				resolve();
			};
			this.gameClient.updateUI();
			this.gameClient.tutorialStateMachine.onTieBreaker();
		});
	}
}
